import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/** Eine Gruppe entspricht einer Kachel auf der Startseite, z. B. „Benjamin Blümchen". */
export interface CatalogGroup {
  id: string;
  title: string;
  imageUrl: string;
  /**
   * Folge, aus der das Bild automatisch uebernommen wurde. Verlaesst diese
   * Folge die Gruppe, waehlt die App automatisch ein neues Bild.
   */
  imageItemId?: string;
  /** Von den Eltern bewusst gesetztes Bild – wird nie automatisch ersetzt. */
  imageManual?: boolean;
  sortIndex: number;
}

/** Eine Folge innerhalb einer Gruppe (ein Spotify-Album oder eine Show). */
export interface CatalogItem {
  id: string;
  groupId: string;
  kind: 'album' | 'show';
  uri: string;
  title: string;
  imageUrl: string;
  /** Anzahl der Kapitel-Tracks – Basis fuer die Fortschrittsberechnung. */
  trackCount: number;
  sortIndex: number;
}

/**
 * Hoerfortschritt einer Folge.
 *
 * Kein Eintrag = die Folge gilt als „neu". `trackIndex` ist zwingend, weil
 * Hoerspiel-Alben aus vielen kurzen Kapiteln bestehen und `positionMs` immer
 * nur innerhalb eines Kapitels gilt.
 */
export interface ProgressEntry {
  itemId: string;
  state: 'inProgress' | 'finished';
  trackIndex: number;
  positionMs: number;
  trackCount: number;
  lastPlayedAt: number;
}

interface SpotiboxDB extends DBSchema {
  groups: {
    key: string;
    value: CatalogGroup;
  };
  items: {
    key: string;
    value: CatalogItem;
    indexes: { byGroup: string };
  };
  progress: {
    key: string;
    value: ProgressEntry;
    indexes: { byLastPlayed: number };
  };
}

const DB_NAME = 'spotibox';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<SpotiboxDB>> | null = null;

export function getDb(): Promise<IDBPDatabase<SpotiboxDB>> {
  dbPromise ??= openDB<SpotiboxDB>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        db.createObjectStore('groups', { keyPath: 'id' });

        const items = db.createObjectStore('items', { keyPath: 'id' });
        items.createIndex('byGroup', 'groupId');

        const progress = db.createObjectStore('progress', { keyPath: 'itemId' });
        progress.createIndex('byLastPlayed', 'lastPlayedAt');
      }
    },
  });

  return dbPromise;
}
